<template>
  <v-dialog
    :model-value="modelValue"
    max-width="620px"
    persistent
    scrollable
    data-testid="tax-calc-version-v2-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card @keydown.esc="close">
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">{{ titleIcon }}</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ titleText }}</div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="cversion-v2-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert
          v-if="isCloneMode && initialData"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="cversion-v2-clone-hint"
        >
          โคลนจากชุดสูตร <strong>{{ initialData.versionCode }}</strong> —
          คัดลอกฟังก์ชันทั้งหมด{{ cloneCountText }}ไปเป็นชุดใหม่ (ชุดใหม่เป็น draft ยังไม่ active)
        </v-alert>

        <v-form ref="form">
          <v-text-field
            v-model="form.versionCode"
            data-testid="cversion-v2-code-input"
            label="Version code *"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-tag-outline"
            placeholder="C1"
            :rules="[rules.required]"
            @update:model-value="form.versionCode = normalizeCode(form.versionCode)"
          />

          <v-text-field
            v-model="form.description"
            data-testid="cversion-v2-description-input"
            label="Description"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-text-box-outline"
            placeholder="รายละเอียดชุดสูตรคำนวณนี้"
            class="mt-2"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn data-testid="cversion-v2-cancel-btn" variant="outlined" :disabled="saving" @click="close">
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          data-testid="cversion-v2-submit-btn"
          color="primary"
          variant="elevated"
          :loading="saving"
          @click="submit"
        >
          <v-icon start>{{ submitIcon }}</v-icon>
          {{ submitText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Tri-mode (create | edit | clone) dialog for a Tax Calculate V2 VERSION — the set of C# formula
 * functions, not the functions themselves. Props/emit conventions mirror
 * `components/TaxQuestionVersionV2Dialog.vue`: `modelValue` + `mode` + `initialData`, emits
 * `update:modelValue` and one `submit` carrying the raw form payload; the parent owns the API call.
 *
 * Field rules follow the backend contract:
 *  - CreateTaxCalcVersionV2Request = { versionCode, description, isActive }
 *  - UpdateTaxCalcVersionV2Request = { versionCode, description, isActive }
 *      → unlike the QUESTION version, versionCode IS editable here; TaxCalcVersionV2BAL.UpdateAsync
 *        re-checks uniqueness within the config version and 400s on a clash.
 *  - CloneTaxCalcVersionV2Request  = { versionCode, description }
 *      → the clone is ALWAYS created inactive server-side, which is why the alert says so and why
 *        this dialog never offers an isActive switch. Activation is a separate, deliberate act on
 *        the tree row's toggle.
 */
export default {
  name: 'TaxCalcVersionV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** 'create' | 'edit' | 'clone' */
    mode: { type: String, default: 'create' },
    /** the calc version DTO being edited / cloned from (null on create) */
    initialData: { type: Object, default: null },
    /** pre-filled code (parent suggests the next free C-code for create/clone) */
    suggestedCode: { type: String, default: '' },
    /** parent-driven submit spinner */
    saving: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      form: this.buildForm(),
      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    isCloneMode() {
      return this.mode === 'clone';
    },
    isCreateMode() {
      return this.mode === 'create';
    },
    titleIcon() {
      if (this.isCloneMode) return 'mdi-content-copy';
      return this.isCreateMode ? 'mdi-plus' : 'mdi-pencil';
    },
    titleText() {
      if (this.isCloneMode) return 'โคลนชุดสูตรคำนวณ (Tax Calculate V2)';
      return this.isCreateMode
        ? 'สร้างชุดสูตรคำนวณ (Tax Calculate V2)'
        : 'แก้ข้อมูลชุดสูตรคำนวณ (Tax Calculate V2)';
    },
    submitIcon() {
      if (this.isCloneMode) return 'mdi-content-copy';
      return this.isCreateMode ? 'mdi-plus' : 'mdi-check';
    },
    submitText() {
      if (this.isCloneMode) return 'โคลน';
      return this.isCreateMode ? 'สร้าง' : 'บันทึก';
    },
    cloneCountText() {
      const n = Number(this.initialData?.functionCount);
      return Number.isFinite(n) && n > 0 ? ` (${n} ฟังก์ชัน)` : '';
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetForm();
    },
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    buildForm() {
      return {
        calcVersionID: null,
        versionCode: '',
        description: '',
        isActive: false,
      };
    },
    resetForm() {
      const base = this.buildForm();
      const d = this.initialData;

      if (this.isCreateMode) {
        base.versionCode = this.normalizeCode(this.suggestedCode);
      } else if (d) {
        base.calcVersionID = d.calcVersionID ?? null;
        base.isActive = Boolean(d.isActive);
        if (this.isCloneMode) {
          base.versionCode = this.normalizeCode(this.suggestedCode);
          base.description = d.description || (d.versionCode ? `clone of ${d.versionCode}` : '');
        } else {
          base.versionCode = d.versionCode || '';
          base.description = d.description || '';
        }
      }

      this.form = base;
      this.$nextTick(() => this.$refs.form?.resetValidation?.());
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async submit() {
      const res = await this.$refs.form?.validate?.();
      if (res && res.valid === false) return;

      const code = this.normalizeCode(this.form.versionCode);
      if (!code) return;

      if (this.isCloneMode) {
        this.$emit('submit', {
          mode: 'clone',
          calcVersionID: this.form.calcVersionID,
          versionCode: code,
          description: this.form.description || '',
        });
        return;
      }

      this.$emit('submit', {
        mode: this.isCreateMode ? 'create' : 'edit',
        calcVersionID: this.form.calcVersionID,
        versionCode: code,
        description: this.form.description || '',
        // A metadata save must never flip the live flag: on create it is false (a new set is a
        // draft), on edit it echoes what the row already is.
        isActive: Boolean(this.form.isActive),
      });
    },
  },
};
</script>
